import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { confidenceStyle } from "../components/VisualSearch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { ClipboardCheck, Plus, X, Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";

const MODE_LABELS = {
  AUTO: "AUTO · Unified production engine",
  A: "A · CLIP-only (baseline)",
  B: "B · CLIP + DINO",
  C: "C · Hybrid + OCR",
  D: "D · Hybrid + OCR + geometry + multi-image",
};

const rankColor = (rank) => {
  if (rank == null) return "text-zinc-400";
  if (rank === 1) return "text-green-600 font-bold";
  if (rank <= 5) return "text-blue-600";
  if (rank <= 10) return "text-amber-600";
  return "text-brand";
};

const EvaluatePage = () => {
  const [items, setItems] = useState([]); // {id,file,url,partno}
  const [modes, setModes] = useState({ AUTO: true, A: true, B: true, C: true, D: true });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const inputRef = useRef(null);

  const addFiles = (files) => {
    const next = Array.from(files).map((f, i) => ({
      id: `${Date.now()}-${i}-${f.name}`,
      file: f,
      url: URL.createObjectURL(f),
      partno: "",
    }));
    setItems((prev) => [...prev, ...next]);
  };

  const activeModes = useMemo(() => Object.keys(modes).filter((m) => modes[m]), [modes]);

  const run = async () => {
    const labeled = items.filter((it) => it.partno.trim());
    if (labeled.length === 0) return toast.error("Add images and enter the correct Part No for each.");
    if (activeModes.length === 0) return toast.error("Select at least one engine.");
    setLoading(true);
    setReport(null);
    try {
      const fd = new FormData();
      const labels = {};
      labeled.forEach((it) => {
        fd.append("files", it.file, it.file.name);
        labels[it.file.name] = it.partno.trim();
      });
      fd.append("labels", JSON.stringify(labels));
      fd.append("modes", JSON.stringify(activeModes));
      const { data } = await axios.post(`${API}/test/evaluate`, fd, { timeout: 600000 });
      setReport(data);
      toast.success(`Evaluated ${data.count} image(s) across ${data.modes.length} engine(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Evaluation failed");
    } finally {
      setLoading(false);
    }
  };

  const baseline = report?.results_by_mode?.A;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 space-y-10">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-4">
          <ClipboardCheck className="w-4 h-4" /> Accuracy Evaluation
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Evaluation Test Set</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Upload real spare-part photos and enter the correct Part No for each. The current matching system runs each
          image through the selected engines and reports Top-1 / Top-5 / Top-10 accuracy, rank, similarity and whether
          the correct part was found. Keep <span className="font-mono">A</span> selected to preserve the CLIP-only baseline.
        </p>
      </div>

      {/* Engine selection */}
      <div className="border rounded-md p-6 bg-card">
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-4">Engines to compare</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.keys(MODE_LABELS).map((m) => (
            <label key={m} className="flex items-center gap-3 cursor-pointer" data-testid={`eval-mode-${m}`}>
              <Checkbox checked={modes[m]} onCheckedChange={(v) => setModes((p) => ({ ...p, [m]: !!v }))} />
              <span className="text-sm font-mono">{MODE_LABELS[m]}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Image list */}
      <div className="border rounded-md bg-card">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-heading font-bold text-lg">Test Images ({items.length})</h2>
          <div className="flex gap-2">
            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
              data-testid="eval-file-input" onChange={(e) => e.target.files && addFiles(e.target.files)} />
            <Button data-testid="eval-add-btn" variant="outline" size="sm" className="gap-2" onClick={() => inputRef.current?.click()}>
              <Plus className="w-4 h-4" /> Add Images
            </Button>
            <Button data-testid="eval-run-btn" size="sm" className="gap-2 uppercase tracking-wider font-semibold"
              disabled={loading || items.length === 0} onClick={run}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
              {loading ? "Evaluating…" : "Run Evaluation"}
            </Button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm">
            No images added. Click “Add Images”, then type the correct Part No for each.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {items.map((it) => (
              <div key={it.id} className="border rounded-md overflow-hidden" data-testid={`eval-item-${it.file.name}`}>
                <div className="aspect-video bg-white flex items-center justify-center p-3 relative">
                  <img src={it.url} alt={it.file.name} className="max-h-full max-w-full object-contain" />
                  <button className="absolute top-2 right-2 bg-background border rounded-sm p-1 hover:text-brand"
                    onClick={() => setItems((p) => p.filter((x) => x.id !== it.id))}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-3 border-t space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{it.file.name}</div>
                  <Input placeholder="Correct Part No…" value={it.partno} className="font-mono h-9 text-sm"
                    data-testid={`eval-partno-${it.file.name}`}
                    onChange={(e) => setItems((p) => p.map((x) => x.id === it.id ? { ...x, partno: e.target.value } : x))} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Report */}
      {report && (
        <div className="space-y-8" data-testid="eval-report">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {report.modes.map((m) => {
              const r = report.results_by_mode[m];
              const isBaseline = m === "A";
              const isAuto = m === "AUTO";
              const misses = r.per_image.filter((p) => p.rank == null || p.rank > 10).length;
              return (
                <div key={m} className={`border rounded-md p-5 bg-card ${isAuto ? "border-brand ring-1 ring-brand/30" : isBaseline ? "" : "border-brand/40"}`} data-testid={`eval-summary-${m}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-mono text-xs px-2 py-0.5 rounded-sm bg-foreground text-background">{m}</span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{isAuto ? "Production" : isBaseline ? "Baseline" : "Diagnostic"}</span>
                  </div>
                  <div className="space-y-2 font-mono text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Top-1</span><span className="font-bold text-brand">{(r.top1_accuracy * 100).toFixed(1)}%</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Top-5</span><span>{(r.top5_accuracy * 100).toFixed(1)}%</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Top-10</span><span>{(r.top10_accuracy * 100).toFixed(1)}%</span></div>
                    <div className="flex justify-between pt-1 border-t"><span className="text-muted-foreground">Missing / &gt;Top-10</span><span className={misses ? "text-brand font-bold" : "text-green-600"}>{misses}</span></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* comparison table */}
          <div className="border rounded-md bg-card overflow-x-auto">
            <div className="p-4 border-b flex items-center gap-2">
              <Trophy className="w-4 h-4 text-brand" />
              <h3 className="font-heading font-bold">Per-Image Rank of Correct Part No</h3>
              <span className="text-xs text-muted-foreground font-mono ml-2">({report.count} images · {report.images_searched.toLocaleString()} searched · candidate recall {(report.candidate_recall * 100).toFixed(1)}% · pool {report.candidate_pool_size})</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b">
                  <th className="px-4 py-3">Image</th>
                  <th className="px-4 py-3">Correct Part No</th>
                  {report.modes.map((m) => <th key={m} className="px-4 py-3 text-center">{m} rank</th>)}
                  {report.modes.map((m) => <th key={`s-${m}`} className="px-4 py-3 text-center">{m} sim</th>)}
                </tr>
              </thead>
              <tbody>
                {(baseline?.per_image || report.results_by_mode[report.modes[0]].per_image).map((row, i) => {
                  const ranks = report.modes.map((m) => report.results_by_mode[m].per_image[i]?.rank);
                  const present = ranks.filter((r) => r != null);
                  const bestRank = present.length ? Math.min(...present) : null;
                  const weak = bestRank == null || bestRank > 10;
                  return (
                  <tr key={i} className={`border-b last:border-0 hover:bg-secondary/40 ${weak ? "bg-brand/5" : ""}`} data-testid={`eval-row-${i}`}>
                    <td className="px-4 py-3 truncate max-w-[180px]">
                      {weak && <span className="mr-2 text-[9px] uppercase tracking-wider bg-brand text-white px-1.5 py-0.5 rounded-sm" data-testid={`eval-weak-${i}`}>weak</span>}
                      {row.filename}
                    </td>
                    <td className="px-4 py-3 font-mono">{row.expected_part}</td>
                    {report.modes.map((m) => {
                      const pr = report.results_by_mode[m].per_image[i];
                      return <td key={m} className={`px-4 py-3 text-center font-mono ${rankColor(pr?.rank)}`}>
                        {pr?.rank ?? "—"}
                      </td>;
                    })}
                    {report.modes.map((m) => {
                      const pr = report.results_by_mode[m].per_image[i];
                      return <td key={`s-${m}`} className="px-4 py-3 text-center font-mono text-xs">{pr?.similarity != null ? `${pr.similarity}%` : "—"}</td>;
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {report.skipped?.length > 0 && (
            <div className="text-xs text-muted-foreground font-mono">Skipped: {report.skipped.map((s) => `${s.filename} (${s.reason})`).join(", ")}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default EvaluatePage;
